import { watchWallet, assertWalletContext } from './rpc/walletEvents';
import { createReadPool } from './rpc/providers';
import { createReadSigner } from './rpc/readSigner';
import { selectRpcUrls } from './rpc/config';
import useWalletPayments from './rpc/useWalletPayments';
import { readLogRange } from './rpc/readLogRange';
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import { ethers } from 'ethers';
import { executeLeverSwapGuarded } from './swap/leverSwap';
import { logSwapDebugReport } from './swap/swapDebug';
import { revokeStarTaxAllowances } from './swap/revokeAllowances';
import './App.css';
import SlotMode from './slot/SlotMode';
import { logRewardDebug } from './slot/game/rewardDebug.js';
import './slot/slot.css';
// Production cascade lock: slot.css is also imported by SlotMode, so the
// manual VANGUARD positioning file must be re-imported here after slot.css.
// Otherwise some production/browser builds can apply slot.css after the manual
// scene anchors and make Jackpot/rails/access behave like absolute viewport nodes.
import './slot/vanguardScenePosition.css';
import './slot/vanguardViewportFrameLock.css';
import './slot/webSceneComposition.css';

import {
  starTaxContractAddress,
  starTaxContractABI,
  jackpotTaxReceiverAddress,
  jackpotTaxReceiverABI
} from './contract';

const ENV_RPC_URLS = [
  process.env.REACT_APP_OP_RPC_1,
  process.env.REACT_APP_OP_RPC_2,
  process.env.REACT_APP_OP_RPC_3,
  process.env.REACT_APP_OP_RPC_4,
].filter((u) => typeof u === 'string' && u.trim().length > 0);

const RPC_URLS = selectRpcUrls(ENV_RPC_URLS);

const STARTAX_DEPLOY_BLOCK =
  process.env.REACT_APP_STARTAX_DEPLOY_BLOCK
    ? Number(process.env.REACT_APP_STARTAX_DEPLOY_BLOCK)
    : null;


const MAX_RANGE = 2000;
const OP_CHAIN_ID_HEX = '0xa';
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

const WALLET_RELOAD_DISCONNECT_KEY = 'STARTAX_FORCE_DISCONNECT_ON_RELOAD';

function getWalletReloadDisconnectMarker() {
  if (typeof window === 'undefined') return 0;
  try {
    const rawValue = window.localStorage?.getItem(WALLET_RELOAD_DISCONNECT_KEY);
    const numericValue = Number(rawValue || 0);
    return Number.isFinite(numericValue) ? numericValue : 0;
  } catch (err) {
    return 0;
  }
}

function shouldForceWalletDisconnectOnBoot() {
  return getWalletReloadDisconnectMarker() > 0;
}

function setWalletReloadDisconnectMarker() {
  if (typeof window === 'undefined') return;
  try {
    const nextValue = getWalletReloadDisconnectMarker() + 1;
    window.localStorage?.setItem(WALLET_RELOAD_DISCONNECT_KEY, String(Math.max(1, nextValue)));
  } catch (err) {
    // Best-effort only: browsers can block localStorage in private/restricted modes.
  }
}

function clearWalletReloadDisconnectMarker() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.removeItem(WALLET_RELOAD_DISCONNECT_KEY);
  } catch (err) {
    // Best-effort only.
  }
}

function AppContent() {

  const [providers, setProviders] = useState([]);
  const rpcCooldownUntilRef = useRef([]);

  const [transactionCounter, setTransactionCounter] = useState(0);
  const lastAcceptedTransactionCounterRef = useRef(0);
  const [transactionMinimum, setTransactionMinimum] = useState(null);
  const [currentReceivers, setCurrentReceivers] = useState({});
  const lastBlockNumberRef = useRef(null);
  const snapshotBlockRef = useRef(-1);
  const starTaxIfaceRef = useRef(null);

  const [userAccount, setUserAccount] = useState(null);
  const [walletConnectNonce, setWalletConnectNonce] = useState(0);
  const [walletChainId, setWalletChainId] = useState(null);
  const [walletError, setWalletError] = useState('');
  const [tokenBalance, setTokenBalance] = useState(0);
  const confirmedReward = useWalletPayments(userAccount, providers, setTokenBalance);
  const [lastSwapAmount, setLastSwapAmount] = useState(0);
  const [lastSwapDebugReport, setLastSwapDebugReport] = useState(null);
  const [lastSuccessfulSwap, setLastSuccessfulSwap] = useState(null);
  const manualDisconnectRef = useRef(shouldForceWalletDisconnectOnBoot());

  useEffect(() => {
    if (!shouldForceWalletDisconnectOnBoot()) return;
    // Important: do NOT clear STARTAX_FORCE_DISCONNECT_ON_RELOAD here.
    // The marker must persist across repeated F5/browser refreshes until the user
    // performs an explicit CONNECT WALLET action inside the dApp. Clearing it on
    // boot caused an alternating connected/disconnected state on repeated reloads.
    manualDisconnectRef.current = true;
    setUserAccount(null);
    setWalletChainId(null);
    setTokenBalance(0);
    setLastSwapAmount(0);
    setLastSwapDebugReport(null);
    setLastSuccessfulSwap(null);
    logRewardDebug('wallet-local-disconnect-after-reload', {
      reason: 'page-reload',
      marker: getWalletReloadDisconnectMarker(),
      persistentUntilManualConnect: true,
    });
  }, []);

  useLayoutEffect(() => {
    document.body.classList.add('startax-mode-slot');
    return () => document.body.classList.remove('startax-mode-slot');
  }, []);

  useLayoutEffect(() => {
    const DESIGN_WIDTH = 1920;
    const DESIGN_HEIGHT = 1080;

    let viewportFitRaf = 0;

    const readViewportSize = () => {
      const root = document.documentElement;
      return {
        // Use the layout viewport instead of visualViewport. visualViewport fires
        // noisy intermediate values during browser zoom / Ctrl+wheel, which was
        // causing the VANGUARD scene to jump and made some controls look detached.
        width: root.clientWidth || window.innerWidth || DESIGN_WIDTH,
        height: root.clientHeight || window.innerHeight || DESIGN_HEIGHT,
      };
    };

    const readNumericCssVar = (styles, name, fallback) => {
      const numericValue = Number.parseFloat(styles.getPropertyValue(name));
      return Number.isFinite(numericValue) ? numericValue : fallback;
    };

    const applyViewportFitScaleNow = () => {
      const root = document.documentElement;
      const { width: viewportWidth, height: viewportHeight } = readViewportSize();
      const rootStyles = window.getComputedStyle(root);

      // Beta 3.2.21: deterministic VANGUARD viewBox fit.
      //
      // The manual 1920x1080 scene intentionally has elements tuned slightly
      // outside the original board (large logo on the left, Sushi/rail/gecko on
      // the right, footer/glow at the bottom). Previous fixes changed the safe
      // frame depending on viewport width, which made some controls drift and
      // appear absolute when the browser window or zoom changed.
      //
      // Use a FIXED virtual frame instead: one constant viewBox contains the
      // 1920x1080 board plus its manual overflow. The browser only scales that
      // whole viewBox; no element receives viewport-dependent coordinates.
      const SAFE_LEFT = 400;
      const SAFE_RIGHT = 380;
      // Taller composition plus the original 115-unit lower reserve.
      const SAFE_BOTTOM = 260 + 115;
      const vanguardUiScale =
        readNumericCssVar(rootStyles, '--startax-scale-1080-base', 0.88) *
        readNumericCssVar(rootStyles, '--startax-resolution-tier', 1) *
        readNumericCssVar(rootStyles, '--startax-scale-fine', 1.05) *
        readNumericCssVar(rootStyles, '--startax-vanguard-subscale', 1);

      const sceneSurfaceWidth = DESIGN_WIDTH + SAFE_LEFT + SAFE_RIGHT;
      const sceneSurfaceHeight = DESIGN_HEIGHT + SAFE_BOTTOM;
      const vanguardFitScale = Math.min(
        1,
        viewportWidth / (sceneSurfaceWidth * vanguardUiScale),
        viewportHeight / (sceneSurfaceHeight * vanguardUiScale),
      ) || 1;

      const webScale = Math.min((viewportWidth - 24) / sceneSurfaceWidth, (viewportHeight - 24) / sceneSurfaceHeight);
      root.style.setProperty('--startax-web-scene-scale', Math.max(0.01, webScale).toFixed(6));
      root.style.setProperty('--startax-web-scene-top', '12px');
      root.style.setProperty('--startax-vanguard-viewport-fit-scale', vanguardFitScale.toFixed(5));
      root.style.setProperty('--startax-vanguard-frame-left', `${SAFE_LEFT}px`);
      root.style.setProperty('--startax-vanguard-frame-right', `${SAFE_RIGHT}px`);
      root.style.setProperty('--startax-vanguard-frame-bottom', `${SAFE_BOTTOM}px`);
      root.style.setProperty('--startax-vanguard-responsive-safe-left', `${SAFE_LEFT}px`);
      root.style.setProperty('--startax-vanguard-responsive-safe-right', `${SAFE_RIGHT}px`);
      root.style.setProperty('--startax-vanguard-responsive-safe-x', `${Math.max(SAFE_LEFT, SAFE_RIGHT)}px`);
      root.style.setProperty('--startax-vanguard-responsive-safe-bottom', `${SAFE_BOTTOM}px`);
      root.style.setProperty('--startax-vanguard-surface-width', `${sceneSurfaceWidth}px`);
      root.style.setProperty('--startax-vanguard-surface-height', `${sceneSurfaceHeight}px`);
      root.style.setProperty('--startax-vanguard-origin-x', `${SAFE_LEFT}px`);
      root.style.setProperty('--startax-vanguard-origin-y', '0px');
      root.classList.add('startax-viewport-fit-ready');
    };

    const scheduleViewportFitScale = () => {
      if (viewportFitRaf) cancelAnimationFrame(viewportFitRaf);
      viewportFitRaf = requestAnimationFrame(() => {
        viewportFitRaf = 0;
        applyViewportFitScaleNow();
      });
    };

    applyViewportFitScaleNow();
    window.addEventListener('resize', scheduleViewportFitScale);
    window.addEventListener('orientationchange', scheduleViewportFitScale);

    return () => {
      if (viewportFitRaf) cancelAnimationFrame(viewportFitRaf);
      window.removeEventListener('resize', scheduleViewportFitScale);
      window.removeEventListener('orientationchange', scheduleViewportFitScale);
      document.documentElement.classList.remove('startax-viewport-fit-ready');
    };
  }, []);

  const refreshWalletState = useCallback(async () => {
    try {
      if (!window.ethereum) return { account: null, chainId: null };
      const [accounts, chainId] = await Promise.all([
        window.ethereum.request({ method: 'eth_accounts' }),
        window.ethereum.request({ method: 'eth_chainId' }),
      ]);
      const account = accounts?.[0] || null;
      const visibleAccount = manualDisconnectRef.current ? null : account;
      setUserAccount(visibleAccount);
      setWalletChainId(chainId || null);
      return { account: visibleAccount, chainId };
    } catch (err) {
      console.error('Error refreshing wallet state:', err);
      setUserAccount(null);
      setWalletChainId(null);
      setWalletError(err?.message || 'Wallet state unavailable');
      return { account: null, chainId: null };
    }
  }, []);

  const requestOptimismNetwork = async () => {
    if (!window.ethereum) return false;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: OP_CHAIN_ID_HEX }],
      });
      setWalletChainId(OP_CHAIN_ID_HEX);
      return true;
    } catch (switchError) {
      if (switchError?.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: OP_CHAIN_ID_HEX,
              chainName: 'Optimism',
              nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
              rpcUrls: ['https://mainnet.optimism.io'],
              blockExplorerUrls: ['https://optimistic.etherscan.io'],
            }],
          });
          setWalletChainId(OP_CHAIN_ID_HEX);
          return true;
        } catch (addError) {
          console.error('Error adding Optimism network:', addError);
          setWalletError(addError?.message || 'Could not add Optimism network');
          return false;
        }
      }

      console.error('Error switching to Optimism:', switchError);
      setWalletError(switchError?.message || 'Could not switch to Optimism');
      return false;
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("Please install MetaMask.");
      return null;
    }
    try {
      setWalletError('');
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });
      const account = accounts?.[0] || null;
      manualDisconnectRef.current = false;
      clearWalletReloadDisconnectMarker();
      setUserAccount(account);
      if (account) setWalletConnectNonce((value) => value + 1);

      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      setWalletChainId(chainId || null);

      if (chainId && chainId.toLowerCase() !== OP_CHAIN_ID_HEX) {
        await requestOptimismNetwork();
        await refreshWalletState();
      }

      return account;
    } catch (err) {
      console.error("Error connecting wallet:", err);
      setWalletError(err?.message || 'Wallet connection failed');
      throw err;
    }
  };

  const disconnectWallet = useCallback(async ({ revokeAllowances = false, onRevokeStep } = {}) => {
    setWalletError('');

    let revokeSummary = null;

    if (revokeAllowances) {
      if (!window.ethereum?.request) {
        throw new Error('Browser wallet is not available.');
      }
      if (!userAccount) {
        throw new Error('Wallet is not connected.');
      }

      let activeChainId = walletChainId;
      if (!activeChainId || activeChainId.toLowerCase() !== OP_CHAIN_ID_HEX) {
        const switched = await requestOptimismNetwork();
        if (!switched) {
          const err = new Error('Switch to Optimism before revoking allowances.');
          err.code = 'WRONG_NETWORK';
          throw err;
        }
        activeChainId = OP_CHAIN_ID_HEX;
      }

      const web3Provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await web3Provider.getSigner(userAccount);
      revokeSummary = await revokeStarTaxAllowances({
        signer,
        ownerAddress: userAccount,
        onStep: onRevokeStep,
      });
    }

    manualDisconnectRef.current = true;
    clearWalletReloadDisconnectMarker();

    if (window.ethereum?.request) {
      try {
        await window.ethereum.request({
          method: 'wallet_revokePermissions',
          params: [{ eth_accounts: {} }],
        });
      } catch (err) {
        console.info('[StarTax wallet] wallet_revokePermissions unavailable or rejected; using local disconnect fallback.', err?.message || err);
      }
    }

    setUserAccount(null);
    setWalletChainId(null);
    setTokenBalance(0);
    setLastSwapAmount(0);
    setLastSwapDebugReport(null);
    setLastSuccessfulSwap(null);
    return { disconnected: true, revokeSummary };
  }, [userAccount, walletChainId]);

  useEffect(() => {
    let wallet;
    try { wallet = window.ethereum; } catch { setWalletError("Browser wallet unavailable"); return undefined; }
    if (!wallet) return undefined;

    refreshWalletState();

    const handleAccountsChanged = (accounts) => {
      const account = accounts?.[0] || null;
      if (manualDisconnectRef.current && account) {
        setUserAccount(null);
        setWalletError('');
        return;
      }
      if (!account) manualDisconnectRef.current = false;
      setUserAccount(account);
      setWalletError('');
    };

    const handleChainChanged = (chainId) => {
      setWalletChainId(chainId || null);
      setWalletError('');
    };

    return watchWallet(wallet, {
      accountsChanged: handleAccountsChanged,
      chainChanged: handleChainChanged,
    }, () => setWalletError('Wallet events unavailable; checking account and network periodically.'), refreshWalletState);
  }, [refreshWalletState]);

  useEffect(() => {
    const markReloadDisconnect = () => {
      // If the dApp is already locally disconnected by a previous reload guard,
      // keep refreshing the marker even when userAccount is hidden/null. This
      // prevents spam-refresh from falling back to eth_accounts auto-restore.
      if (!userAccount && !manualDisconnectRef.current && !shouldForceWalletDisconnectOnBoot()) return;
      setWalletReloadDisconnectMarker();
      manualDisconnectRef.current = true;
      logRewardDebug('wallet-reload-disconnect-marker-set', {
        wallet: userAccount || null,
        reason: 'beforeunload-or-pagehide',
        marker: getWalletReloadDisconnectMarker(),
        persistentUntilManualConnect: true,
      });
    };

    window.addEventListener('beforeunload', markReloadDisconnect);
    window.addEventListener('pagehide', markReloadDisconnect);

    return () => {
      window.removeEventListener('beforeunload', markReloadDisconnect);
      window.removeEventListener('pagehide', markReloadDisconnect);
    };
  }, [userAccount]);

  const applyTransactionCounter = useCallback((nextCounter, source = "unknown") => {
    const numericCounter = Number(nextCounter);
    if (!Number.isFinite(numericCounter) || numericCounter < 0) {
      console.warn("[StarTax counter] Ignored invalid counter from " + source + ":", nextCounter);
      return lastAcceptedTransactionCounterRef.current;
    }

    const currentAccepted = lastAcceptedTransactionCounterRef.current || 0;
    if (numericCounter < currentAccepted) {
      console.warn("[StarTax counter] Ignored stale counter from " + source + ": " + numericCounter + " < " + currentAccepted);
      return currentAccepted;
    }

    lastAcceptedTransactionCounterRef.current = numericCounter;
    setTransactionCounter((previousCounter) => {
      if (numericCounter < previousCounter) {
        console.warn("[StarTax counter] Prevented UI rollback from " + previousCounter + " to " + numericCounter + " (" + source + ")");
        return previousCounter;
      }
      return numericCounter;
    });
    return numericCounter;
  }, []);

  const isCounterFreshEnough = useCallback((candidateCounter, source = "snapshot") => {
    const numericCounter = Number(candidateCounter);
    const currentAccepted = lastAcceptedTransactionCounterRef.current || 0;
    const fresh = Number.isFinite(numericCounter) && numericCounter >= currentAccepted;
    if (!fresh) {
      console.warn("[StarTax counter] Ignored stale " + source + " snapshot: " + numericCounter + " < " + currentAccepted);
    }
    return fresh;
  }, []);

  useEffect(() => {
    const reader=createReadPool(RPC_URLS);
    const list = [reader];
    setProviders(list);
    rpcCooldownUntilRef.current = list.map(() => 0);
    const iface = new ethers.Interface(starTaxContractABI);
    starTaxIfaceRef.current = iface;
    return ()=>reader.destroy();
  }, []);

  const pickProvider = useCallback(() => {
    const list = providers;
    if (!list || list.length === 0) return null;

    const now = Date.now();
    const n = list.length;
    for (let i = 0; i < n; i++) {
      const idx = i;
      if (rpcCooldownUntilRef.current[idx] <= now) {
        return { provider: list[idx], idx };
      }
    }
    return null;
  }, [providers]);

  const markRateLimited = useCallback((rpcIndex) => {
    if (rpcIndex == null) return;
    rpcCooldownUntilRef.current[rpcIndex] = 0;
  }, []);

  const safeGetBlockNumber = useCallback(async (p, rpcIndex) => {
    try {
      return await p.getBlockNumber();
    } catch (e) {
      markRateLimited(rpcIndex);
      throw e;
    }
  }, [markRateLimited]);

  const safeGetBlock = useCallback(async (p, rpcIndex, blockNumber) => {
    try {
      return await p.getBlock(blockNumber);
    } catch (e) {
      markRateLimited(rpcIndex);
      throw e;
    }
  }, [markRateLimited]);

  const safeGetLogs = useCallback(async (p, rpcIndex, args) => {
    try {
      return await p.getLogs(args);
    } catch (e) {
      markRateLimited(rpcIndex);
      throw e;
    }
  }, [markRateLimited]);

  useEffect(() => {
    let cancelled = false;
    let busy = false;
    const run = async () => {
      if (cancelled || busy || lastBlockNumberRef.current !== null) return;
      if (!providers || providers.length === 0 || !starTaxIfaceRef.current) return;

      const picked = pickProvider();
      if (!picked) return;
      const { provider: p, idx } = picked;

      busy = true;
      try {
        const st = new ethers.Contract(starTaxContractAddress, starTaxContractABI, p);
        const blockTag = await safeGetBlockNumber(p, idx);

        const txCount = await st.transactionCounter({ blockTag });
        if (!isCounterFreshEnough(txCount, "initial StarTax data")) return;


        const tMinBN = await st.transactionMinimum({ blockTag });
        const tMinFloat = parseFloat(ethers.formatUnits(tMinBN, 18));


        const [r10, r100, r1K, r10K, r100K, r1M] = await Promise.all([
          st.currentReceiver10({ blockTag }),
          st.currentReceiver100({ blockTag }),
          st.currentReceiver1000({ blockTag }),
          st.currentReceiver10000({ blockTag }),
          st.currentReceiver100000({ blockTag }),
          st.currentReceiver1000000({ blockTag })
        ]);

        if (cancelled || !isCounterFreshEnough(txCount, "initial StarTax data")) return;
        applyTransactionCounter(txCount, "initial StarTax data");
        setTransactionMinimum(Math.ceil(tMinFloat));
        setCurrentReceivers({
          StarTax10: r10,
          StarTax100: r100,
          StarTax1K: r1K,
          StarTax10K: r10K,
          StarTax100K: r100K,
          StarTax1M: r1M,
        });

        const initial = Math.max(0, STARTAX_DEPLOY_BLOCK || 0, blockTag);
        lastBlockNumberRef.current = initial;
        snapshotBlockRef.current = blockTag;
      } catch (err) {
        markRateLimited(idx);
        console.warn("Initial StarTax read failed; retrying another RPC:", err);
      } finally { busy = false; }
    };
    run();
    const retry = setInterval(run, 2000);
    return () => { cancelled = true; clearInterval(retry); };
  }, [providers, pickProvider, safeGetBlockNumber, applyTransactionCounter, isCounterFreshEnough, markRateLimited]);

  useEffect(() => {
    if (!providers || providers.length === 0 || !starTaxIfaceRef.current) return;

    const iface = starTaxIfaceRef.current;

    const topicOf = (name) => iface.getEvent(name).topicHash;
    const topicsAny = [[
      topicOf('TransactionCounterUpdated'),
      topicOf('MinimumTransactionUpdated'),
      topicOf('NewReceiver10'),
      topicOf('NewReceiver100'),
      topicOf('NewReceiver1000'),
      topicOf('NewReceiver10000'),
      topicOf('NewReceiver100000'),
      topicOf('NewReceiver1000000'),
    ]];

    let intervalId = null;

    const processLogs = (logs) => {
      let maxBlock = lastBlockNumberRef.current;
      for (const log of [...logs].sort((a,b) => a.blockNumber-b.blockNumber || (a.index ?? a.logIndex)-(b.index ?? b.logIndex))) {
        if (log.blockNumber <= snapshotBlockRef.current) continue;
        let parsed;
        try { parsed = iface.parseLog(log); } catch { continue; }
        if (!parsed) continue;
        switch (parsed.name) {
          case 'TransactionCounterUpdated': {
            const v = Number(parsed.args.newCounter ?? parsed.args[0]);
            applyTransactionCounter(v, "TransactionCounterUpdated event"); break;
          }
          case 'MinimumTransactionUpdated': {
            const bn = parsed.args.newMinimum ?? parsed.args[0];
            const v = Math.ceil(parseFloat(ethers.formatUnits(bn, 18)));
            setTransactionMinimum(v); break;
          }
          case 'NewReceiver10':
          case 'NewReceiver100':
          case 'NewReceiver1000':
          case 'NewReceiver10000':
          case 'NewReceiver100000':
          case 'NewReceiver1000000': {
            const mapKey = {
              NewReceiver10: 'StarTax10',
              NewReceiver100: 'StarTax100',
              NewReceiver1000: 'StarTax1K',
              NewReceiver10000: 'StarTax10K',
              NewReceiver100000: 'StarTax100K',
              NewReceiver1000000: 'StarTax1M',
            }[parsed.name];
            const argName = Object.keys(parsed.args).find(k =>
              typeof k === 'string' && k.toLowerCase().includes('newreceiver')
            );
            const newRecv = (argName ? parsed.args[argName] : parsed.args[0]);
            setCurrentReceivers(prev => ({ ...prev, [mapKey]: newRecv }));
            break;
          }
          default: break;
        }
        if (log.blockNumber > maxBlock) maxBlock = log.blockNumber;
      }
      return maxBlock;
    };

    let polling = false;
    let cancelled = false;
    const poll = async () => {
      if (polling || cancelled || lastBlockNumberRef.current === null) return;
      polling = true;
      try {
        const picked = pickProvider();
        if (!picked) return;
        const { provider: p, idx } = picked;
        const latest = await safeGetBlockNumber(p, idx);
        const fromBlock = Math.max(lastBlockNumberRef.current + 1, STARTAX_DEPLOY_BLOCK || 0);
        if (fromBlock > latest || cancelled) return;
        const toBlock = Math.min(latest, fromBlock + MAX_RANGE - 1);
        const logs = await readLogRange({
          fromBlock, toBlock, address: starTaxContractAddress, topics: topicsAny,
          getLogs: args => safeGetLogs(p, idx, args),
          getBlock: number => safeGetBlock(p, idx, number),
        });
        if (cancelled) return;
        processLogs(logs);
        lastBlockNumberRef.current = toBlock;
      } catch (err) {
        console.error('Error in unified polling StarTax (RR):', err);
      } finally {
        polling = false;
      }
    };
    intervalId = setInterval(poll, 3000);
    poll();
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [providers, pickProvider, safeGetBlockNumber, safeGetBlock, safeGetLogs, applyTransactionCounter]);

  const [jackpotTotal, setJackpotTotal] = useState(0);
  const [jackpotFiboReceiver, setJackpotFiboReceiver] = useState(ZERO_ADDRESS);
  const [jackpotFibPot, setJackpotFibPot] = useState(0);
  const [jackpotLastFiboNumber, setJackpotLastFiboNumber] = useState(0);

  const jackpotReadPendingRef = useRef(false);
  const refreshJackpotData = useCallback(async () => {
    if (!providers.length || jackpotReadPendingRef.current) return null;
    jackpotReadPendingRef.current = true;
    let failure;
    try {
      for (let attempt = 0; attempt < providers.length; attempt++) {
        const picked = pickProvider();
        if (!picked) break;
        const { provider: p, idx } = picked;
        try {
          const blockTag = await p.getBlockNumber();
          const jContract = new ethers.Contract(jackpotTaxReceiverAddress, jackpotTaxReceiverABI, p);
          const [lastFibo, fibPotBN, chipsTokenAddr, lastFiboNumberBN] = await Promise.all([
            jContract.lastFiboReceiver({ blockTag }),
            jContract.fibPot({ blockTag }),
            jContract.chipsToken({ blockTag }),
            jContract.lastFiboNumber({ blockTag }),
          ]);
          const chipTokenObj = new ethers.Contract(chipsTokenAddr, [
            'function balanceOf(address) view returns (uint256)'
          ], p);
          const balanceBN = await chipTokenObj.balanceOf(jackpotTaxReceiverAddress, { blockTag });
          const fibPot = Math.floor(Number(ethers.formatUnits(fibPotBN, 18)));
          const total = Math.floor(Number(ethers.formatUnits(balanceBN, 18)));
          const fiboNumber = Number(lastFiboNumberBN.toString());
          setJackpotFiboReceiver(lastFibo || ZERO_ADDRESS);
          setJackpotFibPot(fibPot);
          setJackpotTotal(total);
          setJackpotLastFiboNumber(fiboNumber);
          return { jackpotTotal: total, jackpotFiboReceiver: lastFibo || ZERO_ADDRESS, jackpotFibPot: fibPot, jackpotLastFiboNumber: fiboNumber };
        } catch (err) {
          markRateLimited(idx);
          failure = err;
        }
      }
      console.warn('[StarTax jackpot] RPCs unavailable; keeping the last snapshot and retrying.', failure);
      return null;
    } finally { jackpotReadPendingRef.current = false; }
  }, [providers, pickProvider, markRateLimited]);

  useEffect(() => {
    if (!providers || providers.length === 0) return undefined;

    let intervalId = null;
    let cooldownId = null;

    cooldownId = setTimeout(() => {
      refreshJackpotData();
      intervalId = setInterval(refreshJackpotData, 8000);
    }, 1000);

    return () => {
      if (cooldownId) clearTimeout(cooldownId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [providers, refreshJackpotData]);

  const claimJackpot = async (options = {}) => {
    const silent = options?.silent === true;

    try {
      if (!window.ethereum) {
        const err = new Error('Please install MetaMask.');
        if (!silent) {
          alert("Please install MetaMask.");
          return null;
        }
        throw err;
      }
      if (!userAccount) {
        const err = new Error('Please connect your wallet first.');
        if (!silent) {
          alert("Please connect your wallet first.");
          return null;
        }
        throw err;
      }

      if (walletChainId && walletChainId.toLowerCase() !== OP_CHAIN_ID_HEX) {
        const switched = await requestOptimismNetwork();
        if (!switched) throw new Error('Switch to Optimism before claiming the Jackpot.');
      }

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });
      const wallet = window.ethereum;
      await assertWalletContext(wallet, userAccount, OP_CHAIN_ID_HEX);
      const signer = await new ethers.BrowserProvider(wallet).getSigner(accounts[0]);
      const jContract = new ethers.Contract(jackpotTaxReceiverAddress, jackpotTaxReceiverABI, signer);
      const tx = await jContract.claimJackpot();
      const receipt = await tx.wait();
      await refreshJackpotData();

      if (!silent) alert("Jackpot claimed successfully!");
      return { txHash: tx.hash, receipt };
    } catch (err) {
      console.error("Error claiming jackpot =>", err);
      if (!silent) alert("Error claiming the Jackpot: " + (err.reason || err.message || err));
      throw err;
    }
  };

  const refreshSlotOnchainState = async (web3Provider) => {
    try {
      const st = new ethers.Contract(starTaxContractAddress, starTaxContractABI, web3Provider);
      const blockTag = await web3Provider.getBlockNumber();
      const [txCount, r10, r100, r1K, r10K, r100K, r1M] = await Promise.all([
        st.transactionCounter({ blockTag }),
        st.currentReceiver10({ blockTag }),
        st.currentReceiver100({ blockTag }),
        st.currentReceiver1000({ blockTag }),
        st.currentReceiver10000({ blockTag }),
        st.currentReceiver100000({ blockTag }),
        st.currentReceiver1000000({ blockTag }),
      ]);
      if (!isCounterFreshEnough(txCount, "immediate slot refresh")) return;
      if (blockTag < snapshotBlockRef.current) return;
      snapshotBlockRef.current = blockTag;
      applyTransactionCounter(txCount, "immediate slot refresh");
      setCurrentReceivers({
        StarTax10: r10,
        StarTax100: r100,
        StarTax1K: r1K,
        StarTax10K: r10K,
        StarTax100K: r100K,
        StarTax1M: r1M,
      });
    } catch (refreshError) {
      console.warn('[StarTax lever] Immediate state refresh failed. Polling will update shortly.', refreshError);
    }
  };

  const handleLeverSwapGuarded = async ({ mode, swapMultiplier = 1, onStep: onLeverUiStep }) => {
    if (!window.ethereum) throw new Error('Browser wallet is not available.');
    if (!userAccount) throw new Error('Wallet is not connected.');
    if (walletChainId && walletChainId.toLowerCase() !== OP_CHAIN_ID_HEX) {
      throw new Error('Wrong network. Switch to Optimism first.');
    }

    const wallet = window.ethereum;
    await assertWalletContext(wallet, userAccount, OP_CHAIN_ID_HEX);
    const web3Provider = new ethers.BrowserProvider(wallet);
    const walletSigner = await web3Provider.getSigner(userAccount);
    const signer = createReadSigner(walletSigner,providers[0] || web3Provider);

    try {
      const result = await executeLeverSwapGuarded({
        mode,
        swapMultiplier,
        signer,
        walletAddress: userAccount,
        onStep: (stepPayload) => {
          const { step, txHash, approval } = stepPayload || {};
          onLeverUiStep?.(stepPayload);
          if (step === 'dry_run_ready') console.info('[StarTax lever] Dry-run ready. Real send disabled.');
          if (step === 'approval_requested') console.info('[StarTax lever] Approval requested:', approval?.label || approval?.spender);
          if (step === 'approval_confirmed') console.info('[StarTax lever] Approval confirmed:', approval?.label || approval?.spender);
          if (step === 'swap_requested') console.info('[StarTax lever] Swap confirmation requested.');
          if (step === 'swap_submitted') console.info('[StarTax lever] Swap submitted:', txHash);
          if (step === 'swap_confirmed') console.info('[StarTax lever] Swap confirmed.');
          if (step === 'swap_reverted') console.warn('[StarTax lever] Swap reverted:', txHash || 'hash unavailable');
          if (step === 'user_rejected_signature') console.warn('[StarTax lever] User rejected wallet confirmation.');
        },
      });

      const debugReport = logSwapDebugReport(result);
      setLastSwapDebugReport(debugReport);

      if (result?.realExecution?.status === 'confirmed') {
        const confirmedAmount = result.lastSwapAmountDisplay || 0;
        setLastSwapAmount(confirmedAmount);
        setLastSuccessfulSwap({
          amount: confirmedAmount,
          mode: result.mode || mode,
          txHash: result.realExecution?.swapTxHash || debugReport?.txHash || null,
          settledAt: Date.now(),
        });
        await refreshSlotOnchainState(providers[0] || web3Provider);
      }

      return result;
    } catch (error) {
      const result = error?.result;
      if (result) {
        const debugReport = logSwapDebugReport(result);
        setLastSwapDebugReport(debugReport);

        if (result?.realExecution?.status === 'reverted') {
          await refreshSlotOnchainState(web3Provider);
        }
      }
      throw error;
    }
  };

  return (
    <SlotMode
        walletAddress={userAccount}
        walletConnectNonce={walletConnectNonce}
        tokenBalance={tokenBalance}
        transactionMinimum={transactionMinimum}
        confirmedReward={confirmedReward}
        transactionCounter={transactionCounter}
        currentReceivers={currentReceivers}
        lastSwapAmount={lastSwapAmount}
        lastSwapDebugReport={lastSwapDebugReport}
        lastSuccessfulSwap={lastSuccessfulSwap}
        isCorrectNetwork={walletChainId ? walletChainId.toLowerCase() === OP_CHAIN_ID_HEX : true}
        walletError={walletError}
        onConnectWallet={connectWallet}
        onDisconnectWallet={disconnectWallet}
        onLeverSwap={handleLeverSwapGuarded}
        jackpotTotal={jackpotTotal}
        jackpotFiboReceiver={jackpotFiboReceiver}
        jackpotFibPot={jackpotFibPot}
        jackpotLastFiboNumber={jackpotLastFiboNumber}
        onClaimJackpot={claimJackpot}
      />
  );
}

export default AppContent;
